import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getTasks,
  createBulkTasks,
  updateTask,
  bulkUpdateTasks,
  createTasks,
  deleteTask
} from "../lib/taskApi";

// Query Keys Factory: A best practice for consistent query keys.
const taskKeys = {
  all: ["tasks"],
  lists: () => [...taskKeys.all, "list"],
  list: (filters) => [...taskKeys.lists(), { filters }],
  details: () => [...taskKeys.all, "detail"],
  detail: (id) => [...taskKeys.details(), id],
};

export const useGetTasks = (page = 1, limit = 10, user = null, keyword = "", status = "", fromDate = "", toDate = "", project_id="") => {
  const employeeId = user?.role === "employee" ? user?.employeeProfile?._id : undefined;

  return useQuery({
    queryKey: ["tasks", { page, limit, employeeId, keyword, status, fromDate, toDate, project_id }],
    queryFn: () => getTasks(page, limit, employeeId, keyword, status, fromDate, toDate, project_id),
    keepPreviousData: true,
  });
};
/**
 * Hook to create a single task.
 * Primarily for an employee creating one task for themselves.
 */
export const useCreateBulkTasks = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createBulkTasks,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
    onError: (error) => {
      console.error("Bulk task creation failed:", error);
    },
  });
};

/**
 * Hook to create tasks in bulk.
 * This is the primary hook for your dynamic form.
 */
export const useCreateTasks = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createTasks,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
    onError: (error) => {
      console.error("Bulk task creation failed:", error);
    },
  });
};

/**
 * Hook for an HR user to update a task.
 */
export const useUpdateTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    // The mutation function receives an object { taskId, data }
    mutationFn: updateTask,
    onSuccess: (data) => {
      // After a successful update, refetch the entire list.
      queryClient.invalidateQueries({ queryKey: ["tasks"] });

      // OPTIONAL: For even faster UI updates, you can manually update the cache
      // for this specific task if your app has a "detail" view.
      // queryClient.setQueryData(taskKeys.detail(data.task._id), data.task);
    },
    onError: (error) => {
      console.error("Task update failed:", error);
    },
  });
};


/**
 * Hook for HR to update multiple tasks at once (e.g., mark as completed).
 */
export const useBulkUpdateTasks = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: bulkUpdateTasks,
    onSuccess: (data) => {
      // When the bulk update is successful, invalidate the entire task list.
      // This tells React Query to refetch the data, ensuring the UI is always fresh.
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: (error) => {
      console.error("Bulk task update failed:", error);
      // You could add a toast notification here for better UX
    }
  });
};

export const useDeleteTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (taskId) => deleteTask(taskId),
    onSuccess: () => {
      // 🔄 Invalidate tasks list so it refetches
      queryClient.invalidateQueries({ queryKey: ["tasks", "list"] });
    },
    onError: (error) => {
      console.error("❌ Failed to delete task:", error);
    },
  });
};
